/** 주간학습 안내 표 과목행 배경 — 편제 등록 순과 동일 인덱스에 매칭되는 기본 팔레트 */
export const WEEKLY_LEARNING_GUIDE_TRACK_PALETTE_HEX = [
  '#FF5A5E',
  '#FFD200',
  '#FF7800',
  '#A3CF10',
  '#49B017',
  '#5E92D3',
  '#9B86A9',
  '#FF999D',
  '#7693AA',
  '#D2C4E0',
  '#00C38E',
  '#C1AF96',
  '#00A9E9',
  '#C1E4EF',
] as const;

/** track_color_map 안에 함께 저장하는 색상 모드 키(편제명과 겹치지 않는 예약 키) */
export const WEEKLY_GUIDE_COLOR_MODE_KEY = '__color_mode__';

/** 'cell': 시간표 셀 색 반영 · 'track': 편제별 기본색 */
export type WeeklyGuideColorMode = 'cell' | 'track';

/** 시간표셀색 모드에서 셀 색이 없는 칸의 기본 회색 */
export const WEEKLY_GUIDE_CELL_MODE_FALLBACK_HEX = '#A0A4AA';

export function readWeeklyGuideColorMode(saved: Record<string, string> | null | undefined): WeeklyGuideColorMode {
  return (saved?.[WEEKLY_GUIDE_COLOR_MODE_KEY] ?? '').trim() === 'cell' ? 'cell' : 'track';
}

export function buildDefaultWeeklyGuideTrackColorMap(orderedTrackNames: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  orderedTrackNames.forEach((name, index) => {
    const key = name.trim();
    if (!key) return;
    map[key] = WEEKLY_LEARNING_GUIDE_TRACK_PALETTE_HEX[index % WEEKLY_LEARNING_GUIDE_TRACK_PALETTE_HEX.length];
  });
  return map;
}

export function mergeWeeklyGuideTrackColorMap(
  orderedTrackNames: string[],
  saved: Record<string, string>
): Record<string, string> {
  const defaults = buildDefaultWeeklyGuideTrackColorMap(orderedTrackNames);
  const out: Record<string, string> = { ...defaults };
  orderedTrackNames.forEach((name) => {
    const k = name.trim();
    if (!k) return;
    const raw = (saved[k] ?? '').trim();
    if (!raw) return;
    const norm = normalizeWeeklyGuideColorHex(raw);
    if (norm) out[k] = norm;
  });
  return out;
}

export function normalizeWeeklyGuideColorHex(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  let s = t.startsWith('#') ? t : `#${t}`;
  if (s.length === 4) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    s = `#${r}${r}${g}${g}${b}${b}`;
  }
  if (!/^#[0-9A-Fa-f]{6}$/.test(s)) return '';
  return s.toUpperCase();
}

/** 시간표셀색 모드: 연간시간표의 옅은 셀 색을 카드에서 알아볼 수 있게 진하게 보정 */
export function intensifyLightWeeklyGuideColorHex(hex: string): string {
  const norm = normalizeWeeklyGuideColorHex(hex);
  if (!norm) return '';
  const n = parseInt(norm.slice(1), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let l = (max + min) / 2;
  const d = max - min;
  let s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  /** 기준: #E7EDE6 → #89C17F (명도 0.92→0.63, 채도 0.16→0.35) */
  const MAX_LIGHTNESS = 0.627;
  if (l <= MAX_LIGHTNESS) return norm;
  l = MAX_LIGHTNESS;
  if (s >= 0.08) s = s < 0.35 ? Math.min(0.35, s * 2.13) : Math.min(s, 0.55);
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r1, g1, b1] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`.toUpperCase();
}
